import { pick } from "lodash-es";

import SavedAddressType, {
  ReservedSavedAddressType,
} from "@mapmycustomers/shared/enum/SavedAddressType";

import analyticsService from "./AnalyticsService";

// The Segment Events sheet's "Verb: Screen => Action" format, not AnalyticsService's "->"
export enum SavedLocationEvent {
  CAP_REACHED = "Viewed: Saved Locations => Limit Reached",
  CLEARED = "Completed: Saved Locations => Clear Location",
  CREATED = "Completed: Saved Locations => Save Location",
  DELETED = "Completed: Saved Locations => Delete Location",
  NAMED = "Completed: Saved Locations => Name Location",
  PIN_CLICKED = "Clicked: Saved Locations => Map Pin",
  SELECTED = "Clicked: Saved Locations => Select Location",
  UPDATED = "Completed: Saved Locations => Edit Location",
}

export enum SavedLocationCreateSource {
  DROPPED_PIN = "dropped_pin",
  MAP_POI = "map_poi",
  RESERVED_ROW = "reserved_row",
  SEARCH_RESULT = "search_result",
  UNIVERSAL_SEARCH = "universal_search",
}

export enum SavedLocationDeleteSurface {
  MAP_POPOVER = "map_popover",
  POI_STAR = "poi_star",
  RESULT_STAR = "result_star",
  UNIVERSAL_SEARCH = "universal_search",
}

export enum SavedLocationNameSource {
  NAME_STEP = "name_step",
  PIN_CARD_EDIT = "pin_card_edit",
}

export enum SavedLocationSelectSurface {
  ACTIVITY_LOCATION = "activity_location",
  CREATE_ROUTE = "create_route",
  DISTANCE_SORT = "distance_sort",
  GO_TO_LOCATION = "go_to_location",
  MILEAGE_MANUAL = "mileage_manual",
  ROUTE_END = "route_end",
  ROUTE_START = "route_start",
  TEMPORARY_STOP = "temporary_stop",
  UNIVERSAL_SEARCH = "universal_search",
}

export enum SavedLocationUpdatedField {
  ADDRESS = "address",
  NAME = "name",
}

export interface SavedLocationEventProperties {
  [SavedLocationEvent.CAP_REACHED]: { source: SavedLocationCreateSource };
  [SavedLocationEvent.CLEARED]: { kind: ReservedSavedAddressType };
  [SavedLocationEvent.CREATED]: { kind: SavedAddressType; source: SavedLocationCreateSource };
  [SavedLocationEvent.DELETED]: { surface: SavedLocationDeleteSurface };
  [SavedLocationEvent.NAMED]: {
    kind: SavedAddressType;
    renamed: boolean;
    source: SavedLocationNameSource;
  };
  [SavedLocationEvent.PIN_CLICKED]: { kind: SavedAddressType };
  [SavedLocationEvent.SELECTED]: {
    kind: SavedAddressType;
    query_was_empty: boolean;
    surface: SavedLocationSelectSurface;
  };
  [SavedLocationEvent.UPDATED]: {
    field_changed: SavedLocationUpdatedField;
    kind: SavedAddressType;
  };
}

// Spread properties escape excess-property checks, so this allow-list is what keeps PII out.
const EVENT_PROPERTIES: {
  [E in SavedLocationEvent]: Record<keyof SavedLocationEventProperties[E], true>;
} = {
  [SavedLocationEvent.CAP_REACHED]: { source: true },
  [SavedLocationEvent.CLEARED]: { kind: true },
  [SavedLocationEvent.CREATED]: { kind: true, source: true },
  [SavedLocationEvent.DELETED]: { surface: true },
  [SavedLocationEvent.NAMED]: { kind: true, renamed: true, source: true },
  [SavedLocationEvent.PIN_CLICKED]: { kind: true },
  [SavedLocationEvent.SELECTED]: { kind: true, query_was_empty: true, surface: true },
  [SavedLocationEvent.UPDATED]: { field_changed: true, kind: true },
};

type SavedLocationEventArgs = {
  [E in SavedLocationEvent]: [event: E, properties: SavedLocationEventProperties[E]];
}[SavedLocationEvent];

export const trackSavedLocationEvent = (...[event, properties]: SavedLocationEventArgs) =>
  analyticsService.track(event, {
    ...pick(properties, Object.keys(EVENT_PROPERTIES[event])),
    platform: "web",
  });
